import * as Sentry from '@sentry/react';
import { getDB, STORE_NAME_EXPORT } from '../getDB';
import type { SuggestedApp } from '../types';

export type GetAllRecordsUntilTimeOptions = {
  accountId?: SuggestedApp['accountId'];
  minutesInPast: number;
};

/**
 * Get all records not older than a given time (minutes in the past)
 * Returns records from the last X minutes up to now
 * Uses the timestamp index for efficient querying
 */
export async function getAllRecordsUntilTime(
  options: GetAllRecordsUntilTimeOptions,
): Promise<Array<SuggestedApp>> {
  const { accountId, minutesInPast } = options;

  try {
    const db = await getDB();
    const store = db.transaction(STORE_NAME_EXPORT).store;

    // Calculate cutoff timestamp as ISO string (records not older than this)
    const cutoffTimestamp = new Date(
      Date.now() - minutesInPast * 60 * 1000,
    ).toISOString();

    // Use timestamp index to get entries not older than cutoff (most efficient)
    // lowerBound gets all records with timestamp >= cutoffTimestamp
    const timestampIndex = store.index('by-timestamp');
    const range = IDBKeyRange.lowerBound(cutoffTimestamp);
    let suggestions = await timestampIndex.getAll(range);

    // If accountId is provided, filter by account in memory
    if (accountId) {
      suggestions = suggestions.filter(s => s.accountId === accountId);
    }

    return suggestions;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to get all records until time:', error);
    Sentry.captureException(error);
    return [];
  }
}
